import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Store, Star, MessageSquare, Image, Clock, CheckCircle, XCircle, AlertTriangle, Mail, Calendar, Users, Edit3, Save, X, Upload, Youtube, Trash2, ExternalLink, Send, BookOpen, BookX, Gavel, Lock, Briefcase } from '../icons';
import { useAuth } from '../context/AuthContext';
import { getVendorPortal, updateVendorProfile, uploadVendorPhotos, addVendorVideo, deleteVendorPhoto, updateInquiryStatus, requestBiddingAccess, getAppSettings, changePassword, getInquiryMessages, sendInquiryMessage, getEmployees } from '../services/api';
import { joinInquiry, leaveInquiry, onNewMessage, onTyping, onStopTyping, emitTyping, emitStopTyping, onNotificationAlert, UNREAD_CHANGED_EVENT } from '../services/socket';
import StarRating from '../components/StarRating';
import { useConfirm } from '../components/Modal';
import { useLanguage } from '../context/LanguageContext';
import { categoryImage, vendorPhotoSrc } from '../utils/categoryImages';
import mediaUrl from '../utils/mediaUrl';

// Price columns are DECIMAL, which arrive as strings ("200000.00") that
// toLocaleString() leaves unformatted.
const formatNpr = (v) => (v === null || v === undefined || v === '' ? '—' : `Rs. ${Number(v).toLocaleString()}`);

function VendorPortal() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [confirmDialog, ConfirmDialog] = useConfirm();
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const highlightInqId = searchParams.get('inq') ? parseInt(searchParams.get('inq')) : null;
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoCaption, setVideoCaption] = useState('');
  const [addingVideo, setAddingVideo] = useState(false);
  const photoInputRef = useRef(null);
  const [biddingEnabled, setBiddingEnabled] = useState(false);
  const [replyEnabled, setReplyEnabled] = useState(true);
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMsg, setPwMsg] = useState({ text: '', type: '' });
  const { t } = useLanguage();
  const [teamEmployees, setTeamEmployees] = useState([]);
  const teamLoadedRef = useRef(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'vendor') { navigate('/login'); return; }
    loadPortal();
    getAppSettings().then(res => {
      setBiddingEnabled(res.data?.bidding_enabled === 'true');
      setReplyEnabled(res.data?.vendor_reply_enabled !== 'false');
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate, authLoading]);

  // A new inquiry or message arrives as a notification alert; refresh so the
  // inquiry list, counts and unread dots reflect it without a page reload.
  useEffect(() => {
    if (!user || user.role !== 'vendor') return;
    return onNotificationAlert(() => { loadPortal(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (activeTab === 'team' && !teamLoadedRef.current) {
      teamLoadedRef.current = true;
      getEmployees().then(res => setTeamEmployees(res.data)).catch(() => {});
    }
  }, [activeTab]);

  const loadPortal = async () => {
    try {
      const res = await getVendorPortal();
      setData(res.data);
    } catch (err) {
      console.error('Failed to load vendor portal', err);
    } finally {
      setLoading(false);
    }
  };

  const startEditing = () => {
    const v = data.vendor;
    setEditForm({
      business_name: v.business_name || '',
      description: v.description || '',
      phone: v.phone || '',
      email: v.email || '',
      website: v.website || '',
      address: v.address || '',
      city: v.city || '',
      district: v.district || '',
      price_range: v.price_range || 'moderate',
      min_price_npr: v.min_price_npr || '',
      max_price_npr: v.max_price_npr || '',
    });
    setEditing(true);
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await updateVendorProfile(editForm);
      setEditing(false);
      await loadPortal();
    } catch (err) {
      alert('Failed to save: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) formData.append('photos', files[i]);
      await uploadVendorPhotos(formData);
      await loadPortal();
    } catch (err) {
      alert('Failed to upload: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  const handleAddVideo = async () => {
    if (!videoUrl.trim()) return;
    setAddingVideo(true);
    try {
      await addVendorVideo({ youtube_url: videoUrl.trim(), caption: videoCaption.trim() || null });
      setVideoUrl('');
      setVideoCaption('');
      await loadPortal();
    } catch (err) {
      alert('Failed to add video: ' + (err.response?.data?.error || err.message));
    } finally {
      setAddingVideo(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!await confirmDialog({ title: 'Delete Item', message: 'Delete this item?', danger: true, confirmText: 'Delete' })) return;
    try {
      await deleteVendorPhoto(photoId);
      await loadPortal();
    } catch (err) {
      alert('Failed to delete: ' + (err.response?.data?.error || err.message));
    }
  };

  const getYouTubeId = (url) => {
    const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]+)/);
    return match ? match[1] : null;
  };

  if (loading) return <div className="loading-page"><div className="spinner" /></div>;

  // No vendor profile yet
  if (!data || !data.vendor) {
    return (
      <div className="vendor-portal">
        <div className="portal-container">
          <div className="portal-empty">
            <Store size={48} />
            <h2>{t('vendorPortal.welcome')} {user?.first_name}!</h2>
            <p>{t('vendorPortal.noProfile')}</p>
            <small>Email: admin@nepwed.com</small>
          </div>
        </div>
      </div>
    );
  }

  const { vendor, inquiries, reviews, photos } = data;
  const isPending = vendor.approval_status === 'pending';
  const isRejected = vendor.approval_status === 'rejected';
  const isApproved = vendor.approval_status === 'approved';
  // Suspension blocks logging in; a session opened before it lands here
  const isSuspended = vendor.approval_status === 'suspended';
  const imagePhotos = photos.filter(p => p.media_type !== 'youtube');
  const videoPhotos = photos.filter(p => p.media_type === 'youtube');
  const googleReviewUrl = vendor.google_place_id
    ? `https://search.google.com/local/reviews?placeid=${vendor.google_place_id}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(vendor.business_name + ' ' + vendor.city)}`;

  return (
    <div className="vendor-portal">
      <div className="portal-container">
        {/* Portal Header — gradient hero, always on top */}
        <div className="portal-header">
          <div className="portal-header-info">
            <h1>{vendor.business_name}</h1>
            <p className="portal-category">{vendor.category_name} &middot; {vendor.city}</p>
          </div>
          <div className="portal-header-stats">
            <div className="portal-stat">
              <Star size={18} className="star-filled" />
              <span className="portal-stat-value">{vendor.rating}</span>
              <span className="portal-stat-label">{t('vendorPortal.rating')}</span>
            </div>
            <div className="portal-stat">
              <MessageSquare size={18} />
              <span className="portal-stat-value">{vendor.review_count}</span>
              <span className="portal-stat-label">{t('vendorPortal.reviews')}</span>
            </div>
            <div className="portal-stat">
              <Mail size={18} />
              <span className="portal-stat-value">{inquiries.length}</span>
              <span className="portal-stat-label">{t('vendorPortal.inquiries')}</span>
            </div>
          </div>
        </div>

        {/* Approval Status Banners — below hero header */}
        {isPending && (
          <div className="approval-banner pending">
            <Clock size={20} />
            <div>
              <strong>{t('vendorPortal.approvalPending')}</strong>
              <p>{t('vendorPortal.approvalPendingDesc')}</p>
            </div>
          </div>
        )}
        {isRejected && (
          <div className="approval-banner rejected">
            <XCircle size={20} />
            <div>
              <strong>{t('vendorPortal.rejected')}</strong>
              <p>{t('vendorPortal.rejectedDesc')}</p>
            </div>
          </div>
        )}
        {isApproved && (
          <div className="approval-banner approved">
            <CheckCircle size={20} />
            <div>
              <strong>{t('vendorPortal.approved')}</strong>
              <p>{t('vendorPortal.approvedDesc')}</p>
            </div>
          </div>
        )}
        {isSuspended && (
          <div className="approval-banner rejected">
            <XCircle size={20} />
            <div>
              <strong>{t('vendorPortal.suspended')}</strong>
              <p>{t('vendorPortal.suspendedDesc')}</p>
            </div>
          </div>
        )}
        {/* Approval (or an admin reset) emails a temporary password; nudge the
            vendor to replace it. Cleared once they change their password. */}
        {vendor.has_temp_password && !isSuspended && (
          <div className="approval-banner pending">
            <Lock size={20} />
            <div>
              <strong>{t('vendorPortal.tempPasswordTitle')}</strong>
              <p>{t('vendorPortal.tempPasswordDesc')}</p>
            </div>
          </div>
        )}

        {/* Content - gated by approval */}
        {isPending || isRejected || isSuspended ? (
          <div className="portal-gated">
            <AlertTriangle size={32} />
            <h3>{isPending ? 'Awaiting Admin Approval' : 'Access Restricted'}</h3>
            <p>
              {isPending
                ? 'Your vendor portal features will be unlocked once an admin approves your profile. In the meantime, you can view your basic profile information above.'
                : isSuspended
                  ? 'Your vendor account is suspended, so portal features are unavailable. Please contact the admin team for assistance.'
                  : 'Your vendor profile has been rejected. Portal features are unavailable. Please contact the admin team for assistance.'}
            </p>
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className="portal-tabs">
              <button className={`portal-tab ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>{t('vendorPortal.overviewTab')}</button>
              <button className={`portal-tab ${activeTab === 'inquiries' ? 'active' : ''}`} onClick={() => setActiveTab('inquiries')}>
                {t('vendorPortal.inquiriesTab')} {inquiries.length > 0 && <span className="tab-badge">{inquiries.length}</span>}
              </button>
              <button className={`portal-tab ${activeTab === 'nepwed-reviews' ? 'active' : ''}`} onClick={() => setActiveTab('nepwed-reviews')}>
                {t('vendorPortal.nepwedReviewsTab')} ({reviews.length})
              </button>
              <a
                href={googleReviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="portal-tab"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', textDecoration: 'none' }}
              >
                <img src="https://www.google.com/favicon.ico" alt="Google" style={{ width: 14, height: 14 }} />
                {t('vendorPortal.googleReviewsTab')} <ExternalLink size={12} />
              </a>
              <button className={`portal-tab ${activeTab === 'photos' ? 'active' : ''}`} onClick={() => setActiveTab('photos')}>
                {t('vendorPortal.photosVideosTab')} ({photos.length})
              </button>
              {vendor.is_verified && biddingEnabled && (
                <button className={`portal-tab ${activeTab === 'bidding' ? 'active' : ''}`} onClick={() => setActiveTab('bidding')}>
                  <Gavel size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                  {t('vendorPortal.biddingTab')} {vendor.bidding_approved === 'pending' && <span className="tab-badge" style={{ background: '#f59e0b', marginLeft: '4px' }}>!</span>}
                </button>
              )}
              <button className="portal-tab" onClick={() => navigate('/vendor-projects?tab=team')}>
                <Users size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                Team
              </button>
              <button className="portal-tab" onClick={() => navigate('/vendor-projects?tab=projects')}>
                <Briefcase size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                Projects
              </button>
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="portal-section">
                {!editing ? (
                  <>
                    <div className="portal-overview-grid">
                      <div className="portal-info-card">
                        <h3 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>Business Details</span>
                          <button onClick={startEditing} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#be185d', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 600, fontFamily: 'var(--font-body)', textTransform: 'none', letterSpacing: 'normal', padding: '3px 8px', borderRadius: '6px', transition: 'background 0.15s' }}
                            onMouseOver={e => e.currentTarget.style.background='#fdf2f8'} onMouseOut={e => e.currentTarget.style.background='none'}>
                            <Edit3 size={12} /> Edit
                          </button>
                        </h3>
                        <div className="info-rows">
                          <div className="info-row"><span className="info-label">{t('vendorPortal.businessName')}</span><span>{vendor.business_name}</span></div>
                          <div className="info-row"><span className="info-label">Category</span><span>{vendor.category_name}</span></div>
                          <div className="info-row"><span className="info-label">{t('vendorPortal.priceRange')}</span><span className="capitalize">{vendor.price_range}</span></div>
                          <div className="info-row"><span className="info-label">Price (NPR)</span><span>{formatNpr(vendor.min_price_npr)} - {formatNpr(vendor.max_price_npr)}</span></div>
                          <div className="info-row"><span className="info-label">{t('vendorPortal.city')}</span><span>{vendor.city}</span></div>
                          <div className="info-row"><span className="info-label">{t('vendorPortal.district')}</span><span>{vendor.district || '—'}</span></div>
                          <div className="info-row"><span className="info-label">Address</span><span>{vendor.address}</span></div>
                          <div className="info-row"><span className="info-label">{t('vendorPortal.phone')}</span><span>{vendor.phone}</span></div>
                          <div className="info-row"><span className="info-label">Email</span><span>{vendor.email}</span></div>
                          {vendor.website && <div className="info-row"><span className="info-label">Website</span><span>{vendor.website}</span></div>}
                        </div>
                      </div>
                      <div className="portal-info-card">
                        <h3>{t('vendorPortal.performance')}</h3>
                        <div className="performance-stats">
                          <div className="perf-stat">
                            <span className="perf-value">{vendor.rating}</span>
                            <StarRating rating={vendor.rating} />
                            <span className="perf-label">{t('vendorPortal.averageRating')}</span>
                          </div>
                          <div className="perf-stat">
                            <span className="perf-value">{vendor.review_count}</span>
                            <span className="perf-label">{t('vendorPortal.totalReviews')}</span>
                          </div>
                          <div className="perf-stat">
                            <span className="perf-value">{inquiries.filter(i => i.status === 'booked').length}</span>
                            <span className="perf-label">{t('vendorPortal.bookings')}</span>
                          </div>
                          <div className="perf-stat">
                            <span className="perf-value">{inquiries.filter(i => i.status === 'new').length}</span>
                            <span className="perf-label">{t('vendorPortal.newInquiries')}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="portal-info-card full-width">
                      <h3>{t('vendorPortal.description')}</h3>
                      <p className="vendor-description">{vendor.description}</p>
                    </div>
                  </>
                ) : (
                  /* Edit Profile Form */
                  <div className="portal-info-card full-width" style={{ marginTop: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <h3 style={{ margin: 0 }}>{t('vendorPortal.editProfile')}</h3>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={() => setEditing(false)} className="btn btn-outline" style={{ fontSize: '13px', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <X size={14} /> {t('common.cancel')}
                        </button>
                        <button onClick={handleSaveProfile} disabled={saving} className="btn btn-primary" style={{ fontSize: '13px', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Save size={14} /> {saving ? 'Saving...' : t('vendorPortal.saveChanges')}
                        </button>
                      </div>
                    </div>
                    <div className="edit-form-grid">
                      <div className="edit-field">
                        <label>{t('vendorPortal.businessName')}</label>
                        <input type="text" value={editForm.business_name} onChange={(e) => setEditForm({ ...editForm, business_name: e.target.value })} />
                      </div>
                      <div className="edit-field">
                        <label>{t('vendorPortal.phone')}</label>
                        <input type="text" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
                      </div>
                      <div className="edit-field">
                        <label>Email</label>
                        <input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
                      </div>
                      <div className="edit-field">
                        <label>Website</label>
                        <input type="url" value={editForm.website} onChange={(e) => setEditForm({ ...editForm, website: e.target.value })} placeholder="https://" />
                      </div>
                      <div className="edit-field">
                        <label>{t('vendorPortal.city')}</label>
                        <input type="text" value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} />
                      </div>
                      <div className="edit-field">
                        <label>{t('vendorPortal.district')}</label>
                        <input type="text" value={editForm.district} onChange={(e) => setEditForm({ ...editForm, district: e.target.value })} />
                      </div>
                      <div className="edit-field full-span">
                        <label>Address</label>
                        <input type="text" value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} />
                      </div>
                      <div className="edit-field">
                        <label>{t('vendorPortal.priceRange')}</label>
                        <select value={editForm.price_range} onChange={(e) => setEditForm({ ...editForm, price_range: e.target.value })}>
                          <option value="budget">{t('vendorPortal.budgetFriendly')}</option>
                          <option value="moderate">{t('vendorPortal.moderate')}</option>
                          <option value="premium">{t('vendorPortal.premium')}</option>
                          <option value="luxury">{t('vendorPortal.luxury')}</option>
                        </select>
                      </div>
                      <div className="edit-field">
                        <label>{t('vendorPortal.minPrice')}</label>
                        <input type="number" value={editForm.min_price_npr} onChange={(e) => setEditForm({ ...editForm, min_price_npr: e.target.value })} />
                      </div>
                      <div className="edit-field">
                        <label>{t('vendorPortal.maxPrice')}</label>
                        <input type="number" value={editForm.max_price_npr} onChange={(e) => setEditForm({ ...editForm, max_price_npr: e.target.value })} />
                      </div>
                      <div className="edit-field full-span">
                        <label>{t('vendorPortal.description')}</label>
                        <textarea rows={5} value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
                      </div>
                    </div>
                  </div>
                )}

                {/* Change Password */}
                <div className="portal-info-card full-width" style={{ marginTop: '20px' }}>
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Lock size={18} /> {t('vendorPortal.changePassword')}</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginTop: '12px' }}>
                    <div className="edit-field">
                      <label>{t('vendorPortal.currentPassword')}</label>
                      <input type="password" value={pwForm.currentPassword} onChange={(e) => setPwForm({ ...pwForm, currentPassword: e.target.value })} placeholder={t('vendorPortal.currentPasswordPlaceholder')} />
                    </div>
                    <div className="edit-field">
                      <label>{t('vendorPortal.newPassword')}</label>
                      <input type="password" value={pwForm.newPassword} onChange={(e) => setPwForm({ ...pwForm, newPassword: e.target.value })} placeholder={t('vendorPortal.newPasswordPlaceholder')} />
                    </div>
                    <div className="edit-field">
                      <label>{t('vendorPortal.confirmNewPassword')}</label>
                      <input type="password" value={pwForm.confirmPassword} onChange={(e) => setPwForm({ ...pwForm, confirmPassword: e.target.value })} placeholder={t('vendorPortal.confirmPasswordPlaceholder')} />
                    </div>
                  </div>
                  {pwMsg.text && <p style={{ marginTop: '8px', fontSize: '13px', color: pwMsg.type === 'error' ? '#dc2626' : '#16a34a' }}>{pwMsg.text}</p>}
                  <button
                    disabled={pwLoading}
                    onClick={async () => {
                      setPwMsg({ text: '', type: '' });
                      if (!pwForm.currentPassword || !pwForm.newPassword) { setPwMsg({ text: t('vendorPortal.allFieldsRequired'), type: 'error' }); return; }
                      if (pwForm.newPassword.length < 6) { setPwMsg({ text: t('vendorPortal.passwordMinLength'), type: 'error' }); return; }
                      if (pwForm.newPassword !== pwForm.confirmPassword) { setPwMsg({ text: t('vendorPortal.passwordsNoMatch'), type: 'error' }); return; }
                      setPwLoading(true);
                      try {
                        await changePassword({ currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword });
                        setPwMsg({ text: 'Password changed successfully!', type: 'success' });
                        setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                        if (vendor.has_temp_password) loadPortal(); // clears the temporary-password banner
                      } catch (err) {
                        setPwMsg({ text: err.response?.data?.error || 'Failed to change password', type: 'error' });
                      } finally { setPwLoading(false); }
                    }}
                    className="btn btn-primary"
                    style={{ marginTop: '12px', fontSize: '13px', padding: '8px 20px' }}
                  >
                    {pwLoading ? t('vendorPortal.changing') : t('vendorPortal.changePassword')}
                  </button>
                </div>
              </div>
            )}

            {/* Inquiries Tab */}
            {activeTab === 'inquiries' && (
              <InquiriesManager inquiries={inquiries} onReload={loadPortal} replyEnabled={replyEnabled} highlightId={highlightInqId} />
            )}

            {/* NepWed Reviews Tab */}
            {activeTab === 'nepwed-reviews' && (
              <div className="portal-section">
                {reviews.length === 0 ? (
                  <div className="portal-empty-tab">
                    <Star size={36} />
                    <p>{t('vendorPortal.noReviews')}</p>
                  </div>
                ) : (
                  <div className="portal-reviews">
                    {reviews.map((rev) => (
                      <div key={rev.id} className="portal-review-card">
                        <div className="review-header">
                          <div>
                            <strong>{rev.first_name} {rev.last_name}</strong>
                            <StarRating rating={rev.rating} />
                          </div>
                          <span style={{ fontSize: '12px', color: '#6b7280' }}>{new Date(rev.created_at).toLocaleDateString()}</span>
                        </div>
                        {rev.title && <h4>{rev.title}</h4>}
                        <p>{rev.body}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Photos & Videos Tab */}
            {activeTab === 'photos' && (
              <div className="portal-section">
                {/* Upload Controls */}
                <div className="portal-info-card full-width" style={{ marginBottom: '20px' }}>
                  <h3 style={{ marginBottom: '16px' }}>{t('vendorPortal.uploadPhotos')}</h3>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <button
                      onClick={() => photoInputRef.current?.click()}
                      disabled={uploading}
                      className="btn btn-primary"
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' }}
                    >
                      <Upload size={16} /> {uploading ? 'Uploading...' : t('vendorPortal.uploadPhotos')}
                    </button>
                    <input ref={photoInputRef} type="file" accept="image/*" multiple onChange={handlePhotoUpload} style={{ display: 'none' }} />
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>{t('vendorPortal.uploadHint')}</span>
                  </div>

                  <h3 style={{ marginTop: '24px', marginBottom: '12px' }}>{t('vendorPortal.addYoutubeVideo')}</h3>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <input
                      type="url"
                      value={videoUrl}
                      onChange={(e) => setVideoUrl(e.target.value)}
                      placeholder="https://www.youtube.com/watch?v=..."
                      style={{ flex: '1', minWidth: '250px', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}
                    />
                    <input
                      type="text"
                      value={videoCaption}
                      onChange={(e) => setVideoCaption(e.target.value)}
                      placeholder={t('vendorPortal.captionOptional')}
                      style={{ width: '200px', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}
                    />
                    <button
                      onClick={handleAddVideo}
                      disabled={addingVideo || !videoUrl.trim()}
                      className="btn btn-primary"
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' }}
                    >
                      <Youtube size={16} /> {addingVideo ? t('vendorPortal.adding') : t('vendorPortal.addVideo')}
                    </button>
                  </div>
                </div>

                {/* Photos Grid */}
                {imagePhotos.length > 0 && (
                  <>
                    <h3 style={{ marginBottom: '12px', fontSize: '1rem', color: '#374151' }}>{t('vendorPortal.photos')} ({imagePhotos.length})</h3>
                    <div className="portal-photos-grid">
                      {imagePhotos.map((photo) => (
                        <div key={photo.id} className="portal-photo-card" style={{ position: 'relative' }}>
                          <img
                            src={vendorPhotoSrc(photo.photo_url, vendor, 400, 260)}
                            alt={photo.caption || 'Vendor photo'}
                            onError={(e) => {
                              // A photo whose file is gone would otherwise render as a
                              // broken icon; show the category stand-in instead.
                              const fb = categoryImage(vendor?.category_slug, 400, 260);
                              if (e.currentTarget.src !== fb) e.currentTarget.src = fb;
                            }}
                            style={{ width: '100%', height: '200px', objectFit: 'cover', display: 'block' }}
                          />
                          {photo.caption && <div style={{ padding: '8px 12px', fontSize: '12px', color: '#6b7280', background: '#f9fafb' }}>{photo.caption}</div>}
                          <button
                            onClick={() => handleDeletePhoto(photo.id)}
                            style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(220,38,38,0.85)', color: 'white', border: 'none', borderRadius: '6px', padding: '6px', cursor: 'pointer', display: 'flex' }}
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Videos Grid */}
                {videoPhotos.length > 0 && (
                  <>
                    <h3 style={{ margin: '24px 0 12px', fontSize: '1rem', color: '#374151' }}>{t('vendorPortal.videos')} ({videoPhotos.length})</h3>
                    <div className="portal-photos-grid">
                      {videoPhotos.map((vid) => {
                        const ytId = getYouTubeId(vid.photo_url);
                        return (
                          <div key={vid.id} className="portal-photo-card" style={{ position: 'relative' }}>
                            {ytId ? (
                              <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                                <iframe
                                  src={`https://www.youtube.com/embed/${ytId}`}
                                  title={vid.caption || 'Video'}
                                  frameBorder="0"
                                  allowFullScreen
                                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                                />
                              </div>
                            ) : (
                              <a href={vid.photo_url} target="_blank" rel="noreferrer" style={{ display: 'block', padding: '40px', textAlign: 'center', background: '#f3f4f6', color: '#4b5563' }}>
                                <Youtube size={36} /><br />{t('vendorPortal.viewOnYoutube')}
                              </a>
                            )}
                            {vid.caption && <div style={{ padding: '8px 12px', fontSize: '12px', color: '#6b7280', background: '#f9fafb' }}>{vid.caption}</div>}
                            <button
                              onClick={() => handleDeletePhoto(vid.id)}
                              style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(220,38,38,0.85)', color: 'white', border: 'none', borderRadius: '6px', padding: '6px', cursor: 'pointer', display: 'flex' }}
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                {/* Empty state */}
                {photos.length === 0 && (
                  <div className="portal-empty-tab">
                    <Image size={36} />
                    <p>{t('vendorPortal.noPhotosOrVideos')}</p>
                  </div>
                )}
              </div>
            )}

            {/* Bidding Tab */}
            {activeTab === 'bidding' && vendor.is_verified && (
              <BiddingAccessPanel vendor={vendor} onReload={loadPortal} />
            )}
          </>
        )}
      </div>
      {ConfirmDialog}
    </div>
  );
}

/* Bidding access request sub-component */
function BiddingAccessPanel({ vendor, onReload }) {
  const [requesting, setRequesting] = useState(false);
  const [msg, setMsg] = useState(null);
  const navigate = useNavigate();
  const { t } = useLanguage();

  const handleRequest = async () => {
    setRequesting(true);
    setMsg(null);
    try {
      const res = await requestBiddingAccess();
      setMsg({ type: 'success', text: res.data.message || 'Request submitted!' });
      await onReload();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Failed to submit request' });
    } finally {
      setRequesting(false);
    }
  };

  const status = vendor.bidding_approved;

  return (
    <div className="portal-section">
      <div className="portal-info-card full-width" style={{ padding: '32px', textAlign: 'center' }}>
        <Gavel size={48} style={{ color: status === 'approved' ? '#16a34a' : status === 'pending' ? '#f59e0b' : status === 'denied' ? '#dc2626' : '#9ca3af', marginBottom: '16px' }} />

        {!status && (
          <>
            <h3 style={{ margin: '0 0 8px', fontSize: '1.25rem' }}>{t('vendorPortal.vendorBidding')}</h3>
            <p style={{ color: '#6b7280', fontSize: '14px', maxWidth: '500px', margin: '0 auto 20px', lineHeight: 1.6 }}>
              {t('vendorPortal.biddingDesc')}
            </p>
            <button className="btn btn-primary" onClick={handleRequest} disabled={requesting} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '15px', padding: '12px 28px' }}>
              <Gavel size={16} /> {requesting ? t('vendorPortal.requesting') : t('vendorPortal.requestBiddingAccess')}
            </button>
          </>
        )}

        {status === 'pending' && (
          <>
            <h3 style={{ margin: '0 0 8px', fontSize: '1.25rem', color: '#92400e' }}>{t('vendorPortal.biddingPending')}</h3>
            <p style={{ color: '#6b7280', fontSize: '14px', maxWidth: '500px', margin: '0 auto', lineHeight: 1.6 }}>
              {t('vendorPortal.biddingPendingDesc')}
            </p>
            {vendor.bidding_requested_at && (
              <p style={{ color: '#9ca3af', fontSize: '13px', marginTop: '12px' }}>
                {t('vendorPortal.requestedOn')} {new Date(vendor.bidding_requested_at).toLocaleDateString()}
              </p>
            )}
          </>
        )}

        {status === 'approved' && (
          <>
            <h3 style={{ margin: '0 0 8px', fontSize: '1.25rem', color: '#16a34a' }}>
              <CheckCircle size={22} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
              {t('vendorPortal.biddingApproved')}
            </h3>
            <p style={{ color: '#6b7280', fontSize: '14px', maxWidth: '500px', margin: '0 auto 20px', lineHeight: 1.6 }}>
              {t('vendorPortal.biddingApprovedDesc')}
            </p>
            <button className="btn btn-primary" onClick={() => navigate('/bidding')} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '15px', padding: '12px 28px' }}>
              <Gavel size={16} /> {t('vendorPortal.goToBiddingPage')}
            </button>
          </>
        )}

        {status === 'denied' && (
          <>
            <h3 style={{ margin: '0 0 8px', fontSize: '1.25rem', color: '#dc2626' }}>
              <XCircle size={22} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
              {t('vendorPortal.biddingDenied')}
            </h3>
            <p style={{ color: '#6b7280', fontSize: '14px', maxWidth: '500px', margin: '0 auto 20px', lineHeight: 1.6 }}>
              {t('vendorPortal.biddingDeniedDesc')}
            </p>
            <button className="btn btn-outline" onClick={handleRequest} disabled={requesting} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              {requesting ? t('vendorPortal.requesting') : t('vendorPortal.requestAgain')}
            </button>
          </>
        )}

        {msg && (
          <div style={{ marginTop: '16px', padding: '10px 18px', borderRadius: '8px', background: msg.type === 'success' ? '#f0fdf4' : '#fef2f2', color: msg.type === 'success' ? '#16a34a' : '#dc2626', fontSize: '14px', fontWeight: 600, display: 'inline-block' }}>
            {msg.text}
          </div>
        )}
      </div>
    </div>
  );
}

/* Inquiry management sub-component */
function InquiriesManager({ inquiries, onReload, replyEnabled = true, highlightId = null }) {
  const [replyText, setReplyText] = useState('');
  const [replyFile, setReplyFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(null);
  const [activeSection, setActiveSection] = useState('all'); // all, new, booked, unbooked
  const [openThread, setOpenThread] = useState(null); // inquiry id of open conversation
  const [threadMessages, setThreadMessages] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [coupleTyping, setCoupleTyping] = useState(false);
  const typingTimeoutRef = useRef(null);
  const highlightRef = useRef(null);
  const messagesEndRef = useRef(null);
  const { t } = useLanguage();

  // Scroll to highlighted inquiry. The highlighted thread also opens itself
  // (below), which unmounts the list row before this timer fires — so the ref
  // can be gone by then.
  useEffect(() => {
    if (highlightId && highlightRef.current) {
      setTimeout(() => {
        highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [highlightId, inquiries]);

  // Auto-open highlighted inquiry thread
  useEffect(() => {
    if (highlightId && !openThread) {
      loadThread(highlightId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightId]);

  const loadThread = async (inquiryId) => {
    setOpenThread(inquiryId);
    setThreadLoading(true);
    setReplyText('');
    setReplyFile(null);
    try {
      const res = await getInquiryMessages(inquiryId);
      setThreadMessages(res.data);
      // Opening it marked the thread read on the server; let the bell catch up
      window.dispatchEvent(new CustomEvent(UNREAD_CHANGED_EVENT, { detail: { from: 'portal' } }));
    } catch {
      setThreadMessages([]);
    } finally {
      setThreadLoading(false);
    }
  };

  // Scroll to bottom when messages load or new message added
  useEffect(() => {
    if (messagesEndRef.current && openThread) {
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
    }
  }, [threadMessages, openThread]);

  // Socket.IO: join/leave inquiry room for real-time messages
  useEffect(() => {
    if (!openThread) return;
    joinInquiry(openThread);

    const unsubMsg = onNewMessage(({ inquiryId, message }) => {
      if (inquiryId === openThread) {
        setThreadMessages(prev => {
          if (prev.find(m => m.id === message.id)) return prev;
          return [...prev, message];
        });
      }
    });

    const unsubTyping = onTyping(({ inquiryId, senderType }) => {
      if (inquiryId === openThread && senderType === 'couple') setCoupleTyping(true);
    });
    const unsubStopTyping = onStopTyping(({ inquiryId, senderType }) => {
      if (inquiryId === openThread && senderType === 'couple') setCoupleTyping(false);
    });

    return () => {
      leaveInquiry(openThread);
      unsubMsg();
      unsubTyping();
      unsubStopTyping();
    };
  }, [openThread]);

  const handleSendMessage = async (inquiryId) => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      await sendInquiryMessage(inquiryId, replyText.trim(), replyFile || undefined);
      setReplyText('');
      setReplyFile(null);
      // Reload thread messages
      const res = await getInquiryMessages(inquiryId);
      setThreadMessages(res.data);
      await onReload();
    } catch (err) {
      alert('Failed to send message: ' + (err.response?.data?.error || err.message));
    } finally { setSending(false); }
  };

  const handleStatusChange = async (id, status) => {
    setStatusUpdating(id);
    try {
      await updateInquiryStatus(id, status);
      await onReload();
    } catch (err) {
      alert('Failed to update status: ' + (err.response?.data?.error || err.message));
    } finally { setStatusUpdating(null); }
  };

  const sections = {
    all: inquiries,
    new: inquiries.filter(i => i.status === 'new' || i.status === 'replied'),
    booked: inquiries.filter(i => i.status === 'booked'),
    unbooked: inquiries.filter(i => i.status === 'unbooked'),
  };

  const sectionList = sections[activeSection] || [];

  if (inquiries.length === 0) {
    return (
      <div className="portal-section">
        <div className="portal-empty-tab">
          <MessageSquare size={36} />
          <p>{t('vendorPortal.noInquiriesYet')}</p>
        </div>
      </div>
    );
  }

  const statusColors = { new: '#3b82f6', replied: '#8b5cf6', booked: '#16a34a', unbooked: '#dc2626' };

  // Thread view (full conversation)
  if (openThread) {
    const inq = inquiries.find(i => i.id === openThread);
    if (!inq) { setOpenThread(null); return null; }

    return (
      <div className="portal-section">
        {/* Back button */}
        <button
          onClick={() => { setOpenThread(null); setReplyText(''); setReplyFile(null); onReload(); }}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', color: '#6b7280', fontSize: '14px', cursor: 'pointer', marginBottom: '16px', padding: '4px 0' }}
        >
          ← {t('vendorPortal.backToInquiries')}
        </button>

        {/* Conversation header */}
        <div className="portal-info-card full-width" style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
            <div>
              <strong style={{ fontSize: '16px' }}>{inq.first_name} {inq.last_name}</strong>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>{inq.user_email}</div>
            </div>
            <span style={{
              fontSize: '12px', fontWeight: 600, padding: '4px 12px', borderRadius: '12px',
              background: (statusColors[inq.status] || '#6b7280') + '18',
              color: statusColors[inq.status] || '#6b7280',
              textTransform: 'capitalize',
            }}>{inq.status}</span>
          </div>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '10px', fontSize: '13px', color: '#6b7280' }}>
            {inq.wedding_date && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Calendar size={14} /> {new Date(inq.wedding_date).toLocaleDateString('en-NP', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
            )}
            {inq.guest_count && <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Users size={14} /> {inq.guest_count} guests</span>}
            {inq.budget_range && <span>Budget: NPR {inq.budget_range}</span>}
          </div>
          {/* Status buttons */}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
            {inq.status !== 'booked' && (
              <button className="btn btn-outline" onClick={() => handleStatusChange(inq.id, 'booked')} disabled={statusUpdating === inq.id}
                style={{ fontSize: '12px', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '4px', borderColor: '#16a34a', color: '#16a34a' }}>
                <BookOpen size={12} /> {t('vendorPortal.markBooked')}
              </button>
            )}
            {inq.status !== 'unbooked' && (
              <button className="btn btn-outline" onClick={() => handleStatusChange(inq.id, 'unbooked')} disabled={statusUpdating === inq.id}
                style={{ fontSize: '12px', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '4px', borderColor: '#dc2626', color: '#dc2626' }}>
                <BookX size={12} /> {t('vendorPortal.markUnbooked')}
              </button>
            )}
            {(inq.status === 'booked' || inq.status === 'unbooked') && (
              <button className="btn btn-outline" onClick={() => handleStatusChange(inq.id, inq.vendor_reply ? 'replied' : 'new')} disabled={statusUpdating === inq.id}
                style={{ fontSize: '12px', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Mail size={12} /> {t('vendorPortal.moveToActive')}
              </button>
            )}
          </div>
        </div>

        {/* Messages thread */}
        <div style={{ background: '#f9fafb', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '16px', marginBottom: '16px', maxHeight: '400px', overflowY: 'auto' }}>
          {threadLoading ? (
            <div style={{ textAlign: 'center', padding: '24px' }}><div className="spinner-sm" style={{ margin: '0 auto' }} /></div>
          ) : threadMessages.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>{t('vendorPortal.noMessagesYet')}</p>
          ) : (
            threadMessages.map((msg) => (
              <div key={msg.id} style={{
                marginBottom: '12px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: msg.sender_type === 'vendor' ? 'flex-end' : 'flex-start',
              }}>
                <div style={{
                  maxWidth: '80%',
                  background: msg.sender_type === 'vendor' ? '#EFF6FF' : '#fff',
                  border: `1px solid ${msg.sender_type === 'vendor' ? '#BFDBFE' : '#e5e7eb'}`,
                  borderRadius: '12px',
                  padding: '10px 14px',
                }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: msg.sender_type === 'vendor' ? '#1D4ED8' : '#4f46e5', marginBottom: '4px' }}>
                    {msg.sender_type === 'vendor' ? 'You' : `${inq.first_name} ${inq.last_name}`}
                  </div>
                  <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.6, color: '#374151', whiteSpace: 'pre-wrap' }}>{msg.message}</p>
                  {msg.attachment_url && (
                    <a href={mediaUrl(msg.attachment_url)} target="_blank" rel="noopener noreferrer"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '6px', fontSize: '12px', color: '#1D4ED8', textDecoration: 'none' }}>
                      📎 {msg.attachment_name || 'Attachment'}
                    </a>
                  )}
                  <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '4px' }}>
                    {new Date(msg.created_at).toLocaleString()}
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Typing indicator */}
        {coupleTyping && (
          <div style={{ fontSize: '12px', color: '#4f46e5', fontStyle: 'italic', marginBottom: '6px', padding: '0 4px' }}>
            {inq.first_name} {t('vendorPortal.isTyping')}
          </div>
        )}

        {/* Compose message */}
        {replyEnabled ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <textarea
              rows={3}
              value={replyText}
              onChange={(e) => {
                setReplyText(e.target.value);
                if (openThread) {
                  emitTyping(openThread);
                  clearTimeout(typingTimeoutRef.current);
                  typingTimeoutRef.current = setTimeout(() => emitStopTyping(openThread), 2000);
                }
              }}
              placeholder={t('vendorPortal.typeMessage')}
              style={{ padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', resize: 'vertical', fontFamily: 'inherit' }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#6b7280', cursor: 'pointer', padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: '8px', background: '#f9fafb' }}>
                📎 {replyFile ? replyFile.name : t('vendorPortal.attachFile')}
                <input type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" style={{ display: 'none' }}
                  onChange={(e) => setReplyFile(e.target.files[0] || null)} />
              </label>
              {replyFile && <button onClick={() => setReplyFile(null)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '13px' }}>✕ {t('vendorPortal.removeFile')}</button>}
              <div style={{ flex: 1 }} />
              <button
                className="btn btn-primary"
                onClick={() => handleSendMessage(openThread)}
                disabled={sending || !replyText.trim()}
                style={{ fontSize: '13px', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <Send size={14} /> {sending ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        ) : (
          <span style={{ fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }}>{t('vendorPortal.replyDisabled')}</span>
        )}
      </div>
    );
  }

  // Inquiry list view
  return (
    <div className="portal-section">
      {/* Section tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {[
          { key: 'all', label: `${t('vendorPortal.allInquiries')} (${inquiries.length})`, icon: <MessageSquare size={14} /> },
          { key: 'new', label: `${t('vendorPortal.newRepliedTab')} (${sections.new.length})`, icon: <Mail size={14} /> },
          { key: 'booked', label: `Booked (${sections.booked.length})`, icon: <BookOpen size={14} /> },
          { key: 'unbooked', label: `${t('vendorPortal.unbookedTab')} (${sections.unbooked.length})`, icon: <BookX size={14} /> },
        ].map(sec => (
          <button
            key={sec.key}
            onClick={() => setActiveSection(sec.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', border: '1px solid',
              borderColor: activeSection === sec.key ? '#DC143C' : '#d1d5db',
              background: activeSection === sec.key ? '#FEF2F2' : '#fff',
              color: activeSection === sec.key ? '#DC143C' : '#374151',
              borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
            }}
          >
            {sec.icon} {sec.label}
          </button>
        ))}
      </div>

      {sectionList.length === 0 ? (
        <div className="portal-empty-tab">
          <MessageSquare size={36} />
          <p>No {activeSection} inquiries.</p>
        </div>
      ) : (
        <div className="inquiry-list">
          {sectionList.map((inq) => (
            <div key={inq.id} ref={inq.id === highlightId ? highlightRef : undefined}
              className="portal-info-card full-width" style={{ marginBottom: '16px', transition: 'box-shadow 0.3s', cursor: 'pointer', ...(inq.id === highlightId ? { boxShadow: '0 0 0 3px #DC143C44', borderColor: '#DC143C' } : {}) }}
              onClick={() => loadThread(inq.id)}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
                <div>
                  <strong style={{ fontSize: '15px' }}>{inq.first_name} {inq.last_name}</strong>
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>{inq.user_email}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {!inq.vendor_read && (
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#DC143C', display: 'inline-block' }} />
                  )}
                  <span style={{
                    fontSize: '12px', fontWeight: 600, padding: '4px 12px', borderRadius: '12px',
                    background: (statusColors[inq.status] || '#6b7280') + '18',
                    color: statusColors[inq.status] || '#6b7280',
                    textTransform: 'capitalize',
                  }}>{inq.status}</span>
                </div>
              </div>

              {/* Preview of last message */}
              <p style={{ margin: '0 0 8px', fontSize: '14px', color: '#374151', lineHeight: 1.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {inq.vendor_reply ? inq.vendor_reply.substring(0, 100) + (inq.vendor_reply.length > 100 ? '...' : '') : inq.message?.substring(0, 100) + (inq.message?.length > 100 ? '...' : '')}
              </p>

              {/* Details */}
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', fontSize: '12px', color: '#9ca3af' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Clock size={12} /> {new Date(inq.created_at).toLocaleDateString()}</span>
                {inq.message_count > 0 && <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><MessageSquare size={12} /> {inq.message_count} messages</span>}
                {inq.wedding_date && <span><Calendar size={12} /> {new Date(inq.wedding_date).toLocaleDateString()}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default VendorPortal;
